import { useLayoutEffect } from 'react';

/**
 * Čuvar za sekcije koje se lepe i slažu (`.section--stack`).
 *
 * PROBLEM: lepljiva sekcija se prikuje na `top: var(--nav-h)` i ostaje tako
 * dok je sledeća ne prekrije. Ako je sekcija VIŠA od raspoloživog prozora,
 * njen donji deo nikad ne dođe u vidno polje — na nižim ekranima to znači da
 * se, na primer, dugmad ispod paketa u cenovniku ne mogu ni videti ni kliknuti.
 *
 * Zato se svakoj takvoj sekciji meri visina i, kad ne staje, skida joj se
 * lepljivost (atribut `data-stack-off`). Sekcija se tada ponaša obično i
 * ceo sadržaj ostaje dostupan; izgubi se samo efekat slaganja, što je daleko
 * manja šteta od nedostupnog dugmeta.
 *
 * Poziva se jednom, iz App-a.
 */
export default function useStackGuard() {
  // useLayoutEffect, ne useEffect: prva provera mora da se izvrši PRE nego što
  // browser iscrta. Sa običnim efektom sekcije se prvo pojave sa
  // `min-height: 100svh`, pa se odmah skupe kad im se skine lepljivost —
  // vidno poskakivanje rasporeda i veliki doprinos CLS metrici.
  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;

    const navH = () => {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--nav-h');
      return parseFloat(raw) || 62;
    };

    let raf = 0;
    let observer = null;

    const check = () => {
      const available = window.innerHeight - navH();
      document.querySelectorAll('.section--stack').forEach((el) => {
        // offsetHeight ne uključuje transformacije, što je ovde tačno ono
        // što treba — merimo stvarni prostor koji sekcija zauzima u toku.
        const tooTall = el.offsetHeight > available;
        el.toggleAttribute('data-stack-off', tooTall);
      });
    };

    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(check);
    };

    // Prvi prolaz odmah, sinhrono — ne kroz rAF, da se ne propusti prvo
    // iscrtavanje. Kasnije promene idu preko `schedule`.
    check();

    // Sadržaj sekcija stiže asinhrono (projekti iz API-ja, fontovi), pa se
    // meri i na svaku promenu veličine bilo koje od njih, ne samo prozora.
    const attach = () => {
      observer?.disconnect();
      observer = new ResizeObserver(schedule);
      document.querySelectorAll('.section--stack').forEach((el) => observer.observe(el));
      schedule();
    };

    attach();
    window.addEventListener('resize', schedule);
    // Nove sekcije se pojave pri promeni rute
    const mo = new MutationObserver(attach);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
      mo.disconnect();
    };
  }, []);
}
